import DescriptionIcon from '@mui/icons-material/Description';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Avatar, Box, CircularProgress, Container, Divider, Fade, IconButton, Tooltip, Typography, Zoom } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { likeBlog, getBlogLikesAndComments, getBlogLikesAndCommentsOfUser, getAllPublishedBlogs, getBlogByTitle } from '../../API/API';
import { slugToTitle, titleToSlug } from '../../components/common/Util/Util';
import LoginScreen from '../login/LoginScreen';
import OTPVerification from '../login/OTPVerification';
import RegisterUser from '../login/RegisterUser';
import WelcomeScreen from '../login/WelcomeScreen';

const encodeUnicodeToBase64 = (s = '') => btoa(
  encodeURIComponent(s).replace(/%([0-9A-F]{2})/g, (_, p) => String.fromCharCode(parseInt(p, 16)))
);

const PageWrapper = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(8),
  maxWidth: '1000px !important',
  '& *::selection': {
    background: `${theme.palette.primary.main}20`,
    color: theme.palette.primary.main,
  },
  '@keyframes fadeInUp': {
    from: { opacity: 0, transform: 'translateY(30px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  animation: 'fadeInUp 0.8s ease-out',
}));

const BreadcrumbNav = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  position: 'relative',
  padding: theme.spacing(2, 0),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, #E5E7EB 50%, transparent 100%)',
  },
  '& .separator': {
    color: '#9CA3AF',
    fontSize: '14px',
    fontWeight: 500,
  },
  '& a': {
    color: '#6B7280',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.3s ease',
    position: 'relative',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(1),
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}08`,
      transform: 'translateY(-1px)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -2,
      left: '50%',
      width: '0%',
      height: '2px',
      backgroundColor: theme.palette.primary.main,
      transition: 'all 0.3s ease',
      transform: 'translateX(-50%)',
    },
    '&:hover::after': {
      width: '80%',
    },
  },
  '& .current': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    backgroundColor: `${theme.palette.primary.main}10`,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
    fontSize: '14px',
  },
}));

const AuthorSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  borderRadius: theme.spacing(3),
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
  }
}));

const AuthorInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  '& .avatar': {
    width: 64,
    height: 64,
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    border: '3px solid #fff',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px) scale(1.05)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
    },
  },
  '& .name': {
    fontSize: '20px',
    fontWeight: 700,
    color: '#111827',
    marginBottom: theme.spacing(0.5),
    letterSpacing: '-0.02em',
    background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  '& .metadata': {
    color: '#6B7280',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .dot': { color: theme.palette.primary.main, opacity: 0.8, fontSize: '16px' }
  }
}));

const ShareIconButton = styled(IconButton)(({ theme }) => ({
  color: '#6B7280',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    transform: 'translateY(-2px) rotate(8deg)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  }
}));

const BlogTitle = styled(Typography)(({ theme }) => ({
  fontSize: '48px',
  lineHeight: 1.1,
  fontWeight: 800,
  background: 'linear-gradient(135deg, #111827 0%, #374151 50%, #6B7280 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(3),
  letterSpacing: '-0.03em',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -20,
    top: '50%',
    width: '6px',
    height: '70%',
    background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    transform: 'translateY(-50%)',
    borderRadius: '6px',
    boxShadow: `0 0 20px ${theme.palette.primary.main}40`,
  }
}));

const BlogDescription = styled(Typography)(({ theme }) => ({
  fontSize: '22px',
  lineHeight: 1.6,
  color: '#4B5563',
  fontStyle: 'italic',
  marginBottom: theme.spacing(6),
  maxWidth: '90%',
  position: 'relative',
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
  borderRadius: theme.spacing(2),
  border: '1px solid #bae6fd',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: 0,
    width: '80px',
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: '4px',
  }
}));

const MainImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '520px',
  marginBottom: theme.spacing(8),
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  position: 'relative',
  boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
  transition: 'all 0.5s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.02) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
    opacity: 0,
    transition: 'opacity 0.5s ease',
    pointerEvents: 'none',
    zIndex: 2,
  },
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
    '&::after': { opacity: 1 },
    '& img': { transform: 'scale(1.05)' }
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transition: 'transform 0.5s ease',
  }
}));

const ContentSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontSize: '24px',
    fontWeight: 800,
    color: '#111827',
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(4),
    letterSpacing: '-0.03em',
    position: 'relative',
    paddingBottom: theme.spacing(3),
  },
  '& h2': { fontSize: '22px' },
  '& h3': { fontSize: '20px' },
  '& p': {
    fontSize: '16px',
    lineHeight: 1.8,
    color: '#374151',
    marginBottom: theme.spacing(4),
  },
  '& ul, & ol': {
    listStyle: 'none',
    paddingLeft: 0,
    marginBottom: theme.spacing(4),
    '& li': {
      color: '#374151',
      fontSize: '16px',
      lineHeight: 1.8,
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      position: 'relative',
    }
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    margin: theme.spacing(4, 0),
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    border: '1px solid #e2e8f0',
    '& thead': {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      '& th': {
        padding: theme.spacing(1.5),
        textAlign: 'left',
        border: '1px solid #e2e8f0',
        fontWeight: 600,
        fontSize: '0.9rem',
      }
    },
    '& tbody': {
      '& tr:nth-of-type(even)': { backgroundColor: '#f8fafc' },
      '& td': {
        padding: theme.spacing(1.5),
        border: '1px solid #e2e8f0',
        verticalAlign: 'top',
        fontSize: '0.92rem',
      }
    }
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    position: 'relative',
    '&:hover': {
      color: theme.palette.primary.dark,
      '&::after': { width: '100%' }
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -2,
      left: 0,
      width: '0%',
      height: '2px',
      backgroundColor: theme.palette.primary.main,
      transition: 'width 0.3s ease',
    }
  },
  '& .content-image': {
    width: '80%',
    maxWidth: '600px',
    height: 'auto',
    borderRadius: theme.spacing(3),
    margin: theme.spacing(4, 0),
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    cursor: 'pointer',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(59,130,246,0.02) 0%, rgba(29,78,216,0.01) 100%)',
      borderRadius: theme.spacing(3),
      opacity: 0,
      transition: 'opacity 0.3s ease',
      pointerEvents: 'none',
    },
    '&:hover': {
      transform: 'translateY(-4px) scale(1.02)',
      boxShadow: '0 20px 48px rgba(0,0,0,0.12), 0 8px 24px rgba(59,130,246,0.08)',
      '&::before': { opacity: 1 },
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(59,130,246,0.2), 0 20px 48px rgba(0,0,0,0.12)',
    },
    '@media (max-width: 768px)': {
      width: '90%',
      maxWidth: '100%',
      borderRadius: theme.spacing(2),
      margin: theme.spacing(3, 0),
    },
  },
  '& .content-divider': {
    border: 'none',
    height: '3px',
    background: 'linear-gradient(90deg, transparent 0%, #e2e8f0 50%, transparent 100%)',
    margin: theme.spacing(4, 0),
    borderRadius: '2px',
  },
  '& .highlight-box': {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    border: '2px solid #bae6fd',
    borderRadius: theme.spacing(3),
    padding: theme.spacing(4),
    margin: theme.spacing(4, 0),
    position: 'relative',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '6px',
      height: '100%',
      background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
      borderRadius: '0 4px 4px 0',
      boxShadow: `0 0 20px ${theme.palette.primary.main}40`,
    }
  },
  '& .two-column-layout': {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(4),
    margin: theme.spacing(4, 0),
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' }
  },
  '& .column-content': {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    border: '2px dashed #cbd5e1',
    borderRadius: theme.spacing(3),
    padding: theme.spacing(3),
    minHeight: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    fontStyle: 'italic',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}05`,
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    }
  }
}));

const LikeButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 4),
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '2px solid #e2e8f0',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    borderColor: theme.palette.primary.main,
    '& .like-icon': { transform: 'scale(1.2)' }
  },
  '& .like-icon': { transition: 'transform 0.3s ease' },
  '& .count': { color: '#374151', fontWeight: 600, fontSize: '16px' },
  '& svg': { color: '#EF4444', fontSize: 24 }
}));

const getFileIcon = (file) => {
  const fileUrls = [file?.url, file?.href, file?.fileUrl, file];
  const resultUrl = fileUrls.reduce((acc, curr) => {
    if (acc) return acc;
    const s = `${curr || ''}`;
    return (s.startsWith('http') || s.startsWith('/')) ? s : '';
  }, '');
  const ext = (resultUrl?.split?.('.')?.pop?.() || '').split('?')[0].toLowerCase();
  if (ext === 'pdf') return <PictureAsPdfIcon sx={{ color: '#e53935' }} />;
  if (ext === 'doc' || ext === 'docx') return <DescriptionIcon sx={{ color: '#1976d2' }} />;
  if (ext === 'csv' || ext === 'xls' || ext === 'xlsx') return <TableChartIcon sx={{ color: '#43a047' }} />;
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)) return <ImageIcon sx={{ color: '#8e24aa' }} />;
  return <InsertDriveFileIcon sx={{ color: '#607d8b' }} />;
};

const AttachmentsSection = styled(Box)(({ theme }) => ({
  margin: theme.spacing(4, 0, 4, 0),
  padding: theme.spacing(3.5),
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  borderRadius: theme.spacing(3),
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
    borderRadius: '3px 3px 0 0',
  },
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(2.5) },
}));

const AttachmentLink = styled('a')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  color: '#4b5563',
  fontWeight: 500,
  fontSize: '13px',
  textDecoration: 'none',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid #f1f5f9',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  textAlign: 'center',
  minHeight: '100px',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(59,130,246,0.03) 0%, rgba(29,78,216,0.02) 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover, &:focus': {
    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
    color: theme.palette.primary.main,
    borderColor: '#cbd5e1',
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
    '&::before': { opacity: 1 },
    '& .file-icon': { transform: 'scale(1.1)' },
  },
  '& .file-icon': {
    fontSize: 36,
    marginBottom: theme.spacing(1.5),
    transition: 'transform 0.3s ease',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  '& .file-name': {
    fontSize: '11px',
    lineHeight: 1.3,
    wordBreak: 'break-all',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    fontWeight: 600,
    color: '#374151',
    textAlign: 'center',
    padding: '0 4px',
  },
}));

const KnowledgePost = () => {
  const { slug } = useParams();
  const location = useLocation();
  const actualId = location.state?.id || slug;
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [blogData, setBlogData] = useState(location.state?.blogData);
  const [isLoading, setIsLoading] = useState(!location.state?.blogData);
  const [processedContent, setProcessedContent] = useState('');
  const user = useSelector((state) => state.user.user);
  const [localShowLoginScreen, setLocalShowLoginScreen] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showMobileOTP, setShowMobileOTP] = useState(false);
  const [showOTPVarification, setShowOTPVarification] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [requestData, setRequestData] = useState({});

  const extractContentImageDetails = (image) => {
    const url =
      image && `${image}`.startsWith('http')
        ? `${image}`
        : Object.values(image ?? {})[0];

    if (!url) return { fileName: '', url: '' };

    const fileName = url.split('/').pop()?.split('?')[0] ?? '';
    return { fileName, url };
  };

  const processContent = (content, contentImages = []) => {
    if (!content) return '';
    let processedContent = content;

    processedContent = processedContent
      .replace(/\s+/g, ' ')
      .replace(/>\s+</g, '><')
      .replace(/\s+>/g, '>')
      .replace(/>\s+/g, '>')
      .replace(/\n\s*\n/g, '\n')
      .replace(/\s+$/gm, '')
      .replace(/^\s+/gm, '')
      .trim();

    processedContent = processedContent.replace(/(<br\s*\/?>(\s*)?){2,}/gi, '<br>');
    processedContent = processedContent.replace(/(<br\s*\/?>(\s*)?)+(\s*)?<hr[^>]*>/gi, '<hr class="content-divider" />');
    processedContent = processedContent.replace(/<hr[^>]*>(\s*)?(<br\s*\/?>(\s*)?)+/gi, '<hr class="content-divider" />');

    const contentImageDetails = contentImages
      .map((image) => extractContentImageDetails(image))
      .filter(({ url }) => Boolean(url));

    contentImageDetails.forEach(({ url }, index) => {
      if (!url) return;
      const placeholder = `[CONTENT_IMAGE_${index + 1}]`;
      const imageHtml = `<img src="${url}" alt="Content Image ${index + 1}" class="content-image" loading="lazy" />`;
      processedContent = processedContent.replace(placeholder, imageHtml);
    });

    if (contentImageDetails.length) {
      const imageUrlByFileName = contentImageDetails.reduce((map, { fileName, url }) => {
        if (fileName && url) {
          map[fileName] = url;
        }
        return map;
      }, {});

      processedContent = processedContent.replace(/(<img[^>]+src=")([^">]+)(")/gi, (fullMatch, tagStart, originalSrc, tagEnd) => {
        const fileNameFromSrc = originalSrc.split('/').pop()?.split('?')[0] || originalSrc;
        const finalImageUrl = imageUrlByFileName[originalSrc] || imageUrlByFileName[fileNameFromSrc];
        return finalImageUrl ? `${tagStart}${finalImageUrl}${tagEnd}` : fullMatch;
      });
    }

    processedContent = processedContent.replace(
      /<span[^>]*style="[^"]*display:\s*inline-block[^"]*"[^>]*>(<img[^>]*class="content-image"[^>]*>)<\/span>/g,
      '$1'
    );

    processedContent = processedContent.replace(
      /<hr[^>]*>/g,
      '<hr class="content-divider" />'
    );

    processedContent = processedContent.replace(
      /<div[^>]*style="[^"]*background-color: rgb\(248, 250, 252\)[^"]*"[^>]*>/g,
      '<div class="highlight-box">'
    );

    processedContent = processedContent.replace(
      /<div[^>]*style="[^"]*display: flex; gap: 20px[^"]*"[^>]*>/g,
      '<div class="two-column-layout">'
    );

    processedContent = processedContent.replace(
      /<div[^>]*style="[^"]*flex: 1[^"]*"[^>]*>/g,
      '<div class="column-content">'
    );

    return processedContent;
  };

  useEffect(() => {
    const fetchBlogData = async () => {
      const initialBlogData = location.state?.blogData;
      
      if (!initialBlogData) {
        try {
          setIsLoading(true);
          
          if (slug && slug !== 'untitled') {
            try {
              const titleFromSlug = slugToTitle(slug);
              const byTitle = await getBlogByTitle(encodeUnicodeToBase64(titleFromSlug));
              if (byTitle?.result?.[0]) {
                const fetched = byTitle.result[0];
                setBlogData(fetched);
                const processed = processContent(fetched.content, fetched.ContentImages || []);
                setProcessedContent(processed);
                setIsLoading(false);
                return;
              }
            } catch (titleErr) {
              toast.error('Failed to fetch by title, falling back to published API');
            }
          }
          
          const response = await getAllPublishedBlogs('knowledge');
          if (response && response.result) {
            const blog = response.result.find(blog => String(blog.id) === String(actualId));
            if (blog) {
              try {
                if (blog.title) {
                  const byTitle = await getBlogByTitle(encodeUnicodeToBase64(blog.title));
                  const fetched = byTitle?.result?.[0] || blog;
                  setBlogData(fetched);
                  const processed = processContent(fetched.content, fetched.ContentImages || []);
                  setProcessedContent(processed);
                } else {
                  setBlogData(blog);
                  const processed = processContent(blog.content, blog.ContentImages || []);
                  setProcessedContent(processed);
                }
              } catch (innerErr) {
                setBlogData(blog);
                const processed = processContent(blog.content, blog.ContentImages || []);
                setProcessedContent(processed);
              }
            } else {
              toast.error('Post not found', {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
              });
            }
          }
        } catch (error) {
          toast.error('Failed to load post', {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        try {
          if (initialBlogData?.title) {
            const byTitle = await getBlogByTitle(encodeUnicodeToBase64(initialBlogData.title));
            const fetched = byTitle?.result?.[0] || initialBlogData;
            setBlogData(fetched);
            const processed = processContent(fetched.content, fetched.ContentImages || []);
            setProcessedContent(processed);
          } else {
            setBlogData(initialBlogData);
            const processed = processContent(initialBlogData.content, initialBlogData.ContentImages || []);
            setProcessedContent(processed);
          }
        } catch (innerErr) {
          setBlogData(initialBlogData);
          const processed = processContent(initialBlogData.content, initialBlogData.ContentImages || []);
          setProcessedContent(processed);
        }
      }
    };

    fetchBlogData();
  }, [actualId, location.state?.blogData, slug]);

  useEffect(() => {
    const fetchLikesAndComments = async () => {
      try {
              const response = await getBlogLikesAndComments(blogData.id);
      if (response && response.result) {
        setLikesCount(response.result.totalLikes || 0);
      }

        if (user?.ID || user?.id) {
          const userResponse = await getBlogLikesAndCommentsOfUser(blogData.id, user?.ID || user?.id);
          if (userResponse && userResponse.result) {
            setIsLiked(userResponse.result.totalLikes > 0);
          }
        }
      } catch (error) {
        toast.error('Failed to load interactions', {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    };

    if (blogData?.id && blogData?.title && (user?.ID || user?.id)) {
      fetchLikesAndComments();
    }
  }, [blogData?.id, user]);

  const handleLikeClick = async () => {
    if (!user?.ID && !user?.id) {
      toast.error('Please login to like this post', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setLocalShowLoginScreen(true);
      return;
    }

    const blogIdToUse = blogData?.id ?? blogData?.ID ?? blogData?._id ?? actualId;
    const hasTitle = blogData?.title || blogData?.Title;
    if (!blogIdToUse || !hasTitle) {
      toast.error('Invalid post data', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      setIsLiking(true);
      await likeBlog(user?.ID || user?.id, blogIdToUse, !isLiked);
      setIsLiked(!isLiked);
      setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
      toast.success(isLiked ? 'Post unliked!' : 'Post liked!', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      const response = await getBlogLikesAndComments(blogIdToUse);
      if (response && response.result) {
        setLikesCount(response.result.totalLikes || 0);
      }
    } catch (error) {
      toast.error('Failed to like post. Please try again.', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        toast.success('Link copied to clipboard!', {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .catch(err => {
        console.error('Failed to copy URL:', err);
        toast.error('Failed to copy link', {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="600px">
          <CircularProgress size={60} />
        </Box>
      </PageWrapper>
    );
  }

  if (!blogData || !blogData.title) {
    return (
      <PageWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="600px">
          <Typography variant="h5" color="textSecondary">
            Post not found or invalid
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Fade in={true} timeout={800}>
        <Box>
          <BreadcrumbNav>
            <Link to="/">Home</Link>
            <span className="separator">/</span>
            <Link to="/knowledge">Knowledge</Link>
            <span className="separator">/</span>
            <span className="current">{blogData.title}</span>
          </BreadcrumbNav>

          <Zoom in={true} timeout={1000}>
            <AuthorSection>
              <AuthorInfo>
                <Avatar className="avatar" alt="Admin" />
                <Box>
                  <Typography className="name">Admin</Typography>
                  <Typography className="metadata">
                    {formatDate(blogData.publishedDate || blogData.createDatetime)}
                    <span className="dot">•</span>
                    <span>Knowledge</span>
                  </Typography>
                </Box>
              </AuthorInfo>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {(user?.ID || user?.id) && (
                  <LikeButton onClick={handleLikeClick}>
                    {isLiking ? (
                      <CircularProgress size={20} color="error" />
                    ) : isLiked ? (
                      <FavoriteIcon className="like-icon" />
                    ) : (
                      <FavoriteBorderIcon className="like-icon" />
                    )}
                    <Typography className="count">
                      {likesCount} {likesCount === 1 ? 'like' : 'likes'}
                    </Typography>
                  </LikeButton>
                )}
                <Tooltip title="Copy link to clipboard">
                  <ShareIconButton onClick={handleShare}>
                    <ShareOutlinedIcon />
                  </ShareIconButton>
                </Tooltip>
              </Box>
            </AuthorSection>
          </Zoom>

          <Fade in={true} timeout={1200}>
            <BlogTitle variant="h1">
              {blogData.title}
            </BlogTitle>
          </Fade>

          {blogData.subtitle && Array.isArray(blogData.subtitle) && blogData.subtitle.length > 0 && (
            <Fade in={true} timeout={1400}>
              <BlogDescription>
                {blogData.subtitle.join(' ')}
              </BlogDescription>
            </Fade>
          )}

          {blogData.coverImage && (
            <Zoom in={true} timeout={1600}>
              <MainImage>
                {(() => {
                  const coverImageString = `${blogData.coverImage || ''}`;
                  const coverImageUrl = (coverImageString.startsWith('http') || coverImageString.startsWith('/'))
                    ? coverImageString
                    : (blogData.coverImage && Object.values(blogData.coverImage)[0]);
                  return (
                    <img src={coverImageUrl} alt={blogData.title} />
                  );
                })()}
              </MainImage>
            </Zoom>
          )}

          <Fade in={true} timeout={2000}>
            <ContentSection
              component="div"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />
          </Fade>

          {blogData.images && blogData.images.length > 0 && (
            <AttachmentsSection>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, pb: 2.5, borderBottom: '1px solid #e2e8f0', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(59,130,246,0.2)', mr: 2 }}>
                    <InsertDriveFileIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5, fontSize: '18px' }}>
                      Related Documents
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '13px', fontWeight: 500 }}>
                      Download additional files
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ fontSize: 12, color: '#6b7280', fontWeight: 600, backgroundColor: '#f1f5f9', px: 2, py: 1, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  {blogData.images.length} file{blogData.images.length !== 1 ? 's' : ''}
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2.5, '@media (max-width: 600px)': { gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 2 } }}>
                {blogData.images.map((file, idx) => {
                  const fileString = `${file ?? ''}`;
                  const directUrl = (fileString.startsWith('http') || fileString.startsWith('/')) ? fileString : '';
                  const [nameFromObject, urlFromObject] = Object.entries(file || {})[0] || [];
                  const downloadUrl = directUrl || `${urlFromObject || file?.url || file?.href || file?.fileUrl || ''}`;
                  const displayName = directUrl
                    ? (fileString.split ? decodeURIComponent(fileString.split('/').pop().split('?')[0]) : 'Attachment')
                    : (nameFromObject || file?.name || 'Attachment');
                  return (
                    <AttachmentLink href={downloadUrl || '#'} target={downloadUrl ? "_blank" : undefined} rel={downloadUrl ? "noopener noreferrer" : undefined} key={idx} download tabIndex={0}>
                      <div className="file-icon">{getFileIcon(file)}</div>
                      <div className="file-name">{displayName}</div>
                    </AttachmentLink>
                  );
                })}
              </Box>
            </AttachmentsSection>
          )}

          <Divider sx={{ my: 3 }} />

          {/* <CommentSection blogId={blogData.id} /> */}
        </Box>
      </Fade>
      {localShowLoginScreen && (
        <LoginScreen
          showLoginScreen={localShowLoginScreen}
          setShowLoginScreen={setLocalShowLoginScreen}
          setShowRegister={setShowRegister}
          setShowOTPVarification={setShowOTPVarification}
          setShowMobileOTP={setShowMobileOTP}
          setRequestData={setRequestData}
          requestData={requestData}
        />
      )}
      {showRegister && (
        <RegisterUser
          showRegister={showRegister}
          setShowRegister={setShowRegister}
          setShowLoginScreen={setLocalShowLoginScreen}
          setShowOTPVarification={setShowOTPVarification}
          setRequestData={setRequestData}
          requestData={requestData}
          setShowMobileOTP={setShowMobileOTP}
        />
      )}
      {showOTPVarification && (
        <OTPVerification
          showOTPVarification={showOTPVarification}
          setShowOTPVarification={setShowOTPVarification}
          showMobileOTP={showMobileOTP}
          setShowWelcome={setShowWelcome}
          setRequestData={setRequestData}
          requestData={requestData}
          redirectToApplyLoan={false}
        />
      )}
      {showWelcome && (
        <WelcomeScreen
          showWelcome={showWelcome}
          setShowWelcome={setShowWelcome}
          requestData={requestData}
          redirectToApplyLoan={false}
        />
      )}
    </PageWrapper>
  );
};

export default KnowledgePost;


